import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import type { FragmentResponse } from '../api';
import { guessLanguageFromName, languageLabel } from '../editor/languages';
import { detectFragmentLanguage } from './fragmentNaming';
import { LanguagePicker } from '../components/LanguagePicker';
import { CopyButton } from '../components/CopyButton';
import { Button } from '../components/ui';

const AUTOSAVE_DELAY_MS = 800;

/** Shorter than the autosave, so a detected language is part of the write rather than a second one. */
const DETECT_DELAY_MS = 500;

/** How long the "detected X · Undo" chip stays offering the way back. */
const DETECTION_NOTICE_MS = 8_000;

// CodeMirror is the single heaviest dependency here. Loading it only once an editor is actually
// shown keeps it out of the initial bundle, alongside the language modes it pulls in on demand.
const CodeEditor = lazy(async () => ({
  default: (await import('../editor/CodeEditor')).CodeEditor,
}));

type SaveState = 'saved' | 'unsaved' | 'saving';

interface FragmentEditorProps {
  fragment: FragmentResponse;
  canEdit: boolean;
  onSave: (input: { name: string; language: string; content: string }) => Promise<void>;
  /** Reported on every keystroke so the tab above renames as you type, not a second later. */
  onNameChange: (name: string) => void;
  onRemove: () => void;
}

export function FragmentEditor({
  fragment,
  canEdit,
  onSave,
  onNameChange,
  onRemove,
}: FragmentEditorProps) {
  const [name, setName] = useState(fragment.name);
  const [language, setLanguage] = useState(fragment.language);
  const [content, setContent] = useState(fragment.content);
  const [state, setState] = useState<SaveState>('saved');

  // True once the user has changed something. Without it, the autosave effect fires on mount and
  // on every tab switch, writing values nobody edited.
  const dirty = useRef(false);

  // Whether the language was picked deliberately. Renaming a file should follow the extension,
  // but only until somebody overrides it — after that the name is not evidence any more.
  const languageChosen = useRef(false);

  // Whether the current language is one nobody has asserted — the default, or one we guessed.
  // Content detection may only overwrite that. A fragment that arrives already set to something
  // was set by a person, possibly in another session, and guessing over it would undo their work
  // with no way to tell it had happened.
  const languageUnclaimed = useRef(fragment.language === 'text');

  const [detection, setDetection] = useState<{ from: string; to: string } | null>(null);

  const latest = useRef({ name, language, content });
  latest.current = { name, language, content };

  // No effect syncing state back from props. SnippetDetail keys this component by fragment id, so
  // switching tabs remounts it with fresh initial state — and re-seeding from props on every
  // change would let a refetch that lands mid-keystroke overwrite what is being typed.

  async function save() {
    if (!dirty.current || !canEdit) {
      return;
    }

    // Cleared before awaiting, so a flush-on-unmount and a debounce that both fire do not send
    // the same write twice.
    dirty.current = false;
    setState('saving');

    try {
      await onSave(latest.current);
      setState('saved');
    } catch {
      // Stay unsaved so the indicator keeps telling the truth and Ctrl+S can retry.
      dirty.current = true;
      setState('unsaved');
    }
  }

  // Debounced autosave: one write after the typing stops, not one per keystroke.
  useEffect(() => {
    if (!dirty.current) {
      return;
    }

    setState('unsaved');
    const timer = setTimeout(() => void save(), AUTOSAVE_DELAY_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, language, content]);

  // Flush on the way out. Switching tabs or adding a fragment unmounts this component, and the
  // pending debounce timer dies with it — so an edit made in the last second before switching
  // was silently discarded.
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(
    () => () => {
      if (dirty.current) {
        void saveRef.current();
      }
    },
    [],
  );

  function markDirty() {
    dirty.current = true;
  }

  /**
   * Asks before deleting, because a fragment does not go to the trash the way a snippet does —
   * the row and its content are gone, and nothing in the app brings them back.
   *
   * An empty one is waved through: that is the fragment "+ Fragment" just made, there is nothing
   * to lose, and a prompt over nothing is what teaches people to dismiss the ones that matter.
   * The check reads the live editor value rather than the saved copy, so text typed inside the
   * autosave debounce still counts as content worth asking about.
   */
  function confirmRemove() {
    if (content.trim() === '') {
      onRemove();
      return;
    }

    const question =
      `Delete "${name}"? Fragments do not go to the trash, so its contents cannot be recovered.`;

    if (window.confirm(question)) {
      onRemove();
    }
  }

  /**
   * Sets the language from the code itself, for fragments whose name gives nothing away.
   *
   * Precedence runs deliberate pick, then filename, then content — so naming something `deploy.sh`
   * still wins outright, and this only fills the gap where the name says nothing at all.
   */
  async function detectFromContent(text: string) {
    if (!canEdit || languageChosen.current || !languageUnclaimed.current) {
      return;
    }

    const guess = await detectFragmentLanguage(latest.current.name, text);

    // Re-checked after the await: loading the signature table takes a moment, and a language
    // picked during it is a deliberate choice this must not land on top of.
    if (languageChosen.current || !languageUnclaimed.current) {
      return;
    }

    if (!guess || guess === latest.current.language) {
      return;
    }

    markDirty();
    setDetection({ from: latest.current.language, to: guess });
    setLanguage(guess);
  }

  function chooseLanguage(next: string) {
    markDirty();
    languageChosen.current = true;
    languageUnclaimed.current = false;
    setDetection(null);
    setLanguage(next);
  }

  // Detect after a pause in typing. Pasting does not wait for this — it calls through directly,
  // because a paste arrives complete and there is nothing to wait for.
  useEffect(() => {
    if (!dirty.current) {
      return;
    }

    const timer = setTimeout(() => void detectFromContent(latest.current.content), DETECT_DELAY_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  // The chip is an offer to undo, not a permanent label. Left up it would read as a status.
  useEffect(() => {
    if (!detection) {
      return;
    }

    const timer = setTimeout(() => setDetection(null), DETECTION_NOTICE_MS);

    return () => clearTimeout(timer);
  }, [detection]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-border-subtle px-4 py-2">
        <input
          value={name}
          disabled={!canEdit}
          aria-label="Fragment name"
          onChange={(event) => {
            markDirty();
            const nextName = event.target.value;
            setName(nextName);
            onNameChange(nextName);

            // Follow the extension unless the language was chosen deliberately.
            //
            // The earlier version of this only adopted a guess when the *previous* name also
            // implied one, which quietly failed on the most common path there is: a fragment
            // named after its snippet ("Deploy the widget service") renamed to "deploy.sh".
            const guessed = guessLanguageFromName(nextName);
            if (guessed && !languageChosen.current) {
              setLanguage(guessed.id);
            }
          }}
          className="min-w-32 flex-1 rounded-md border border-transparent bg-transparent px-2 py-1
            font-mono text-sm text-text hover:border-border-subtle focus:border-border-subtle
            disabled:opacity-60"
        />

        <SaveIndicator state={state} canEdit={canEdit} />

        {detection && (
          <DetectionNotice
            language={detection.to}
            onUndo={() => chooseLanguage(detection.from)}
          />
        )}

        <LanguagePicker value={language} disabled={!canEdit} onChange={chooseLanguage} />

        <CopyButton text={content} />

        {canEdit && (
          <Button variant="ghost" onClick={confirmRemove} aria-label={`Delete fragment ${name}`}>
            Delete
          </Button>
        )}
      </div>

      <div className="min-h-0 flex-1">
        <Suspense
          fallback={
            <pre className="h-full overflow-auto px-4 py-3 font-mono text-xs text-text-muted">
              {content}
            </pre>
          }
        >
          <CodeEditor
            value={content}
            language={language}
            readOnly={!canEdit}
            onSave={() => void save()}
            onChange={(next, meta) => {
              markDirty();
              setContent(next);

              if (meta.pasted) {
                void detectFromContent(next);
              }
            }}
          />
        </Suspense>
      </div>
    </div>
  );
}

/**
 * Says what the language was changed to, and offers the way back.
 *
 * A language that changes by itself with no explanation reads as a bug. Announced politely so a
 * screen reader hears it without the change interrupting whatever is being typed.
 */
function DetectionNotice({ language, onUndo }: { language: string; onUndo: () => void }) {
  return (
    <span
      role="status"
      aria-live="polite"
      className="flex items-center gap-1 rounded-md bg-accent-subtle px-2 py-0.5 text-xs text-text-muted"
    >
      Detected {languageLabel(language)}
      <button
        type="button"
        onClick={onUndo}
        className="rounded px-1 text-accent underline-offset-2 hover:underline"
      >
        Undo
      </button>
    </span>
  );
}

function SaveIndicator({ state, canEdit }: { state: SaveState; canEdit: boolean }) {
  if (!canEdit) {
    return <span className="text-xs text-text-faint">Read only</span>;
  }

  const text = state === 'saved' ? 'Saved' : state === 'saving' ? 'Saving…' : 'Unsaved';

  return (
    <span
      role="status"
      aria-live="polite"
      className={`text-xs ${state === 'unsaved' ? 'text-text-muted' : 'text-text-faint'}`}
    >
      {text}
    </span>
  );
}
